import {
  Context,
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  useReducer,
  useState,
} from 'react'
import userReducer, { Action } from './providerReducer'
import { ProviderModel } from '@provider/typing'
import { IOption } from '@survey/typing'
import { BaseUserModel, PatientModel } from './typing'
import { Roles } from '@survey/context/RoleProvider'

export interface IProviderProfileContext<T extends BaseUserModel> {
  user: T
  role: Roles
  setUser: (user: T, role: Omit<Roles, 'general'>) => void
  updateResponse: (
    sectionId: string,
    questionId: string,
    response: IOption[],
    branchingResponse?: IOption[]
  ) => void
  updateLastResponse: (
    sectionId: string,
    questionId: string,
    surveyKey?: string
  ) => void
  updateShowOnProfile: (sectionId: string, questionId: string) => void
  updateImportance: (
    sectionId: string,
    questionId: string,
    importance: 1 | 2 | 3
  ) => void
  setFreeText: (sectionId: string, questionId: string, freeText: string) => void
  updateOtherSelected: (sectionId: string, questionId: string) => void
}

const defaultValue: IProviderProfileContext<BaseUserModel> = {
  user: {} as ProviderModel,
  role: 'general',
  setUser: () => {},
  updateResponse: () => {},
  updateLastResponse: () => {},
  updateShowOnProfile: () => {},
  updateImportance: () => {},
  setFreeText: () => {},
  updateOtherSelected: () => {},
}

export const UserContextRoot: Context<IProviderProfileContext<BaseUserModel>> =
  createContext(defaultValue)

const UserContext: FC<PropsWithChildren> = ({ children }) => {
  const [providerProfileState, dispatch] = useReducer(
    userReducer,
    defaultValue.user
  ) as [ProviderModel | PatientModel, Dispatch<Action>]
  const [role, setRole] = useState<Roles>('general')

  const setUser = <T extends BaseUserModel>(
    profile: T,
    role: Omit<Roles, 'general'>
  ) => {
    dispatch({
      type: 'LOAD_USER',
      payload: { value: profile },
    })
    setRole(role as Roles)
  }

  const updateResponse = (
    sectionId: string,
    questionId: string,
    response: IOption[],
    branchingResponse?: IOption[]
  ) => {
    dispatch({
      type: 'UPDATE_RESPONSE',
      payload: { sectionId, questionId, response, branchingResponse },
    })
  }

  const updateShowOnProfile = (sectionId: string, questionId: string) => {
    dispatch({
      type: 'TOGGLE_SHOW_ON_PROFILE',
      payload: { sectionId, questionId },
    })
  }

  const updateImportance = (
    sectionId: string,
    questionId: string,
    importance: 1 | 2 | 3
  ) => {
    dispatch({
      type: 'SET_IMPORTANT',
      payload: { sectionId, questionId, importance },
    })
  }

  const updateOtherSelected = (sectionId: string, questionId: string) => {
    dispatch({
      type: 'TOGGLE_OTHER_SELECTED',
      payload: { sectionId, questionId },
    })
  }

  const setFreeText = (
    sectionId: string,
    questionId: string,
    freeText: string,
    isBranchingQuestion?: boolean
  ) => {
    dispatch({
      type: 'SET_FREE_TEXT_RESPONSE',
      payload: { sectionId, questionId, freeText, isBranchingQuestion },
    })
  }

  const updateLastResponse = (
    sectionId: string,
    questionId: string,
    surveyKey?: string
  ) => {
    dispatch({
      type: 'UPDATE_LAST_RESPONSE',
      payload: { sectionId, questionId, surveyKey },
    })
  }

  return (
    <UserContextRoot.Provider
      value={{
        user: providerProfileState,
        role,
        setUser,
        updateImportance,
        updateResponse,
        updateLastResponse,
        updateOtherSelected,
        updateShowOnProfile,
        setFreeText,
      }}
    >
      {children}
    </UserContextRoot.Provider>
  )
}

export default UserContext
